/** @jsxImportSource @emotion/react */
import { css, Theme } from '@emotion/react';
import React, { useMemo, useCallback, useEffect, ReactNode } from 'react';
import { DownshiftState, PropGetters } from 'downshift';
import Fuse from 'fuse.js';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';

import { Box, Flex } from 'components/styled';
import Text from 'components/styled/Text';

import { CommonSelectProps } from './SelectInput';

const ITEM_HEIGHT = 40;

type MemoOption<T> = { key: string; name: string; label: ReactNode; option: T };

type Props<T> = {
	options: MemoOption<T>[];
} & Required<
	Pick<CommonSelectProps<T>, 'searchKeys' | 'noOptionsText' | 'unsorted'>
> &
	Pick<CommonSelectProps<T>, 'moveToTop'> &
	Pick<PropGetters<T>, 'getMenuProps' | 'getItemProps'> &
	Omit<DownshiftState<T>, 'selectedItem'> & {
		isSelected: (item: T) => boolean;
	};

function SelectInputMenu<T>({
	options,
	searchKeys,
	getMenuProps,
	getItemProps,
	highlightedIndex,
	inputValue,
	isOpen,
	isSelected,
	noOptionsText,
	unsorted,
	moveToTop,
}: Props<T>): JSX.Element {
	const fuseFilter = useMemo(
		() =>
			new Fuse((options ?? []) as MemoOption<T>[], {
				threshold: 0.4,
				keys: searchKeys,
			}),
		[options, searchKeys],
	);

	// Update fuse when options change
	useEffect(() => fuseFilter.setCollection(options ?? []), [
		fuseFilter,
		options,
	]);

	const filteredOptions = useMemo(() => {
		if (inputValue) {
			return fuseFilter.search(inputValue).map(i => i.item);
		}

		const sortedOptions = unsorted
			? options
			: options?.sort((lhs, rhs) =>
					lhs.name.localeCompare(rhs.name, 'cs-CZ'),
			  ) ?? [];

		const newOptions = moveToTop
			? [
					...sortedOptions.filter(
						o => o.option[moveToTop.field] === moveToTop.value,
					),
					...sortedOptions.filter(
						o => o.option[moveToTop.field] !== moveToTop.value,
					),
			  ]
			: sortedOptions;
		return newOptions;
	}, [fuseFilter, inputValue, options, unsorted, moveToTop]);

	const drawItem = useCallback(
		({ index, style }: ListChildComponentProps) => {
			const item = filteredOptions[index];
			return (
				<Flex
					alignItems="center"
					px={2}
					backgroundColor={
						highlightedIndex === index ? 'primaryLight' : 'transparent'
					}
					{...getItemProps({
						key: item.key,
						item: item.option,
						index,
						style,
					})}
				>
					<Box
						title={item.name}
						fontWeight={isSelected(item.option) ? 'bold' : 'normal'}
						css={css`
							white-space: nowrap;
							text-overflow: ellipsis;
							overflow: hidden;
						`}
					>
						{item.label}
					</Box>
				</Flex>
			);
		},
		[filteredOptions, getItemProps, highlightedIndex, isSelected],
	);

	return (
		<Flex
			{...getMenuProps({})}
			flexDirection="column"
			css={(theme: Theme) => css`
				position: absolute;
				top: 100%;
				width: 100%;
				height: ${Math.max(Math.min(filteredOptions.length, 10), 1) *
				ITEM_HEIGHT}px;
				background-color: white;
				border: 1px solid ${theme.colors.primary};
				z-index: 100000;
				box-sizing: content-box;

				${!isOpen &&
				css`
					display: none;
				`}
			`}
		>
			{isOpen && filteredOptions.length > 0 ? (
				<AutoSizer>
					{({ width, height }) => (
						<List
							itemSize={ITEM_HEIGHT}
							itemCount={filteredOptions.length}
							width={width - 2}
							height={height - 2}
						>
							{drawItem}
						</List>
					)}
				</AutoSizer>
			) : (
				<Text
					bg="lightGrey"
					m={0}
					p={2}
					fontStyle="italic"
					color="textLight"
					ellipsis
					title={noOptionsText}
				>
					{noOptionsText}
				</Text>
			)}
		</Flex>
	);
}
export default SelectInputMenu;
